import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BRAND } from '../../config/brand';
import { ROUTES, ROUTE_LABELS, type Route } from '../routes/routes';
import { parseConvertParams } from '../convertParams';
import { Rail } from './Rail';
import { ErrorBoundary } from './ErrorBoundary';
import { Inspector } from './Inspector';
import { RestoreBanner } from './RestoreBanner';
import { ShortcutsHelp } from './ShortcutsHelp';
import { UpdateBanner } from './UpdateBanner';
import { registerServiceWorker, type ApplyUpdate } from '../sw/register';
import { NavContext, type NavValue } from './navContext';
import { RouteView } from '../routes/RouteView';
import { SessionProvider } from '../session/SessionContext';
import { CustomPresetsProvider } from '../qc/CustomPresetsProvider';
import { QcProvider } from '../qc/QcProvider';
import { BatchProvider } from '../batch/BatchProvider';
import { StudioVideoProvider } from '../studio/StudioVideoProvider';
import { LicenseProvider } from '../license/LicenseProvider';
import { LicensePanel } from '../license/LicensePanel';
import { ThemeProvider } from '../theme/ThemeProvider';
import { ThemeToggle } from '../theme/ThemeToggle';
import { AnalyticsToggle } from '../analytics/AnalyticsToggle';
import { useQc } from '../qc/context';
import { useSession } from '../session/context';
import { useLicense } from '../license/context';
import {
  StudioFileDropContext,
  useWindowFileDrop,
  WindowDropDelegateContext,
  type WindowDropFilesHandler,
} from '../session/useWindowFileDrop';
import { inTextField } from '../keyboard';
import { track } from '../analytics/client';
import { refPathFrom } from '../analytics/catalog';
import '../styles/tokens.css';
import './AppShell.css';

/**
 * Top-level application chrome (spec §8; cue-forge-a26 two-mode IA): left rail
 * (Studio / Convert), a central work area driven by the active rail item, and a
 * right-hand inspector panel for QC issues (Studio only). No router dependency —
 * the modes are local view state, which keeps the initial bundle within the §6
 * budget. The LicenseProvider wraps everything so Pro elements gate consistently
 * (all inline now — there are no whole-route Pro gates). ThemeProvider sits
 * outermost so the light/dark choice (cue-forge-c6b) themes the whole tree.
 */
export function AppShell() {
  return (
    <ThemeProvider>
      <LicenseProvider>
        <SessionProvider>
          <CustomPresetsProvider>
            <QcProvider>
              <BatchProvider>
                <StudioVideoProvider>
                  <ErrorBoundary>
                    <Workspace />
                  </ErrorBoundary>
                </StudioVideoProvider>
              </BatchProvider>
            </QcProvider>
          </CustomPresetsProvider>
        </SessionProvider>
      </LicenseProvider>
    </ThemeProvider>
  );
}

/** Bare /app/ opens Studio (the hero); a `?to=` convert deep-link (e.g. the SEO
 *  convert pages) opens Convert instead. */
function initialRoute(): Route {
  const search = typeof window === 'undefined' ? '' : window.location.search;
  return parseConvertParams(search).to ? 'convert' : 'edit';
}

// Inspector resize bounds + persisted prefs (cue-forge-2bn).
const INSPECTOR_MIN_W = 240;
const INSPECTOR_MAX_W = 560;
const INSPECTOR_DEFAULT_W = 300;
const INSPECTOR_W_KEY = 'cueforge.inspector.width';
const INSPECTOR_COLLAPSED_KEY = 'cueforge.inspector.collapsed';

const clampInspectorW = (n: number) =>
  Math.max(INSPECTOR_MIN_W, Math.min(INSPECTOR_MAX_W, Math.round(n)));

function initInspectorWidth(): number {
  try {
    const v = Number(localStorage.getItem(INSPECTOR_W_KEY));
    return Number.isFinite(v) && v > 0 ? clampInspectorW(v) : INSPECTOR_DEFAULT_W;
  } catch {
    return INSPECTOR_DEFAULT_W;
  }
}

function initInspectorCollapsed(): boolean {
  try {
    return localStorage.getItem(INSPECTOR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Inner chrome, rendered inside all providers so it can read license state. */
function Workspace() {
  const [route, setRoute] = useState<Route>(initialRoute);
  // 1-based cue index the Editor should bring into view (Inspector → Edit).
  const [pendingCueJump, setPendingCueJump] = useState<number | null>(null);
  // Narrow-viewport drawer state for the Inspector (CSS hides the toggle on
  // desktop, where the Inspector is a fixed grid column).
  const [inspectorOpen, setInspectorOpen] = useState(false);
  // Desktop resize + collapse of the Inspector (cue-forge-2bn), persisted.
  const [inspectorWidth, setInspectorWidth] = useState(initInspectorWidth);
  const [inspectorCollapsed, setInspectorCollapsed] = useState(initInspectorCollapsed);
  // Move focus when the operator collapses/reveals the Inspector so it never
  // falls to <body> (WCAG 2.4.3, cue-forge-zxw.9). A ref flag gates it to user
  // actions — the initial persisted state must not steal focus on mount.
  const revealRef = useRef<HTMLButtonElement>(null);
  const pendingInspectorFocus = useRef<'reveal' | 'inspector' | null>(null);
  useEffect(() => {
    const target = pendingInspectorFocus.current;
    if (target === null) return;
    pendingInspectorFocus.current = null;
    if (target === 'reveal') {
      revealRef.current?.focus();
    } else {
      document
        .getElementById('cf-inspector')
        ?.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        ?.focus();
    }
  }, [inspectorCollapsed]);
  // Set when the service worker has a newer version installed and waiting.
  const [applyUpdate, setApplyUpdate] = useState<ApplyUpdate | null>(null);
  // Window-level file-drop safety net (cue-forge-drt.7): a drop outside a
  // dedicated zone (e.g. onto a loaded Studio) must load the file, not let the
  // browser navigate away and destroy the session. The net must match the
  // active mode's own dropzone semantics (cue-forge-w28.14): in Convert mode it
  // delegates to the Converter's registered intake (batch-discard confirm,
  // multi-file → batch list) instead of loading invisibly into the session;
  // Studio keeps the plain session.load, taking the first file of a multi-drop
  // WITH a visible notice about the rest (never a silent discard).
  const { load } = useSession();
  const { isPro } = useLicense();
  const convertDropRef = useRef<WindowDropFilesHandler | null>(null);
  const dropDelegate = useMemo(
    () => ({
      set: (handler: WindowDropFilesHandler | null) => {
        convertDropRef.current = handler;
      },
    }),
    [],
  );
  const [dropNotice, setDropNotice] = useState('');
  const onDroppedFiles = useCallback(
    (files: File[]) => {
      const first = files[0];
      if (!first) return;
      if (route === 'convert' && convertDropRef.current) {
        setDropNotice('');
        convertDropRef.current(files);
        return;
      }
      setDropNotice('');
      const rest = files.length - 1;
      const restNote =
        rest > 0
          ? `${rest} more ${rest === 1 ? 'file was' : 'files were'} ignored — Studio edits one file at a time. To convert several files at once, drop them in Convert.`
          : '';
      // The notice must reflect what the load actually did: `load` can park the
      // file behind the unsaved-edits confirm, fail parsing, or Pro-lock — only
      // a real 'loaded' outcome may claim "Opened X" (the other branches surface
      // their own UX). The ignored-extras fact is true regardless of outcome.
      void load(first, isPro).then((outcome) => {
        if (restNote === '') return;
        setDropNotice(outcome === 'loaded' ? `Opened ${first.name}. ${restNote}` : restNote);
      });
    },
    [route, load, isPro],
  );
  useWindowFileDrop(onDroppedFiles);
  const {
    issues,
    hasRun,
    runCount,
    navTarget,
    stepNext,
    stepPrev,
    fixAndAdvance,
    acceptAndAdvance,
  } = useQc();
  // The QC issues panel belongs with Studio, which now hosts QC (cue-forge-a26);
  // Convert has no cues to inspect, so it runs full-width.
  const showInspector = route === 'edit';

  // Register the offline service worker (production only) and surface updates.
  useEffect(() => {
    registerServiceWorker((apply) => setApplyUpdate(() => apply));
  }, []);

  // A completed QC run opens the Inspector drawer, so narrow-viewport users see
  // the results — issues, or a clean pass plus the report-export buttons
  // (cue-forge-n3e.6) — instead of an apparently-empty screen. Keyed on runCount
  // so it fires only on an explicit run, not on every edit-driven re-evaluation.
  useEffect(() => {
    if (runCount > 0) setInspectorOpen(true);
  }, [runCount]);

  // Persist the Inspector width + collapsed prefs (cue-forge-2bn).
  useEffect(() => {
    try {
      localStorage.setItem(INSPECTOR_W_KEY, String(inspectorWidth));
    } catch {
      /* private mode / storage disabled — pref is best-effort */
    }
  }, [inspectorWidth]);
  useEffect(() => {
    try {
      localStorage.setItem(INSPECTOR_COLLAPSED_KEY, inspectorCollapsed ? '1' : '0');
    } catch {
      /* best-effort */
    }
  }, [inspectorCollapsed]);

  // Drag the separator: the inspector hugs the right edge, so its width is the
  // distance from the pointer to that edge, clamped.
  const onResizePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    const move = (ev: PointerEvent) =>
      setInspectorWidth(clampInspectorW(window.innerWidth - ev.clientX));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, []);

  // Keyboard resize on the separator (WCAG: role=separator must be operable).
  const onResizeKeyDown = useCallback((e: React.KeyboardEvent) => {
    const STEP = 16;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setInspectorWidth((w) => clampInspectorW(w + STEP)); // wider (grows leftward)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setInspectorWidth((w) => clampInspectorW(w - STEP));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setInspectorWidth(INSPECTOR_MAX_W);
    } else if (e.key === 'End') {
      e.preventDefault();
      setInspectorWidth(INSPECTOR_MIN_W);
    }
  }, []);

  // One app_open per load (the sole analytics event allowed to fire from an
  // effect rather than a user gesture). Reads the mount-time license value —
  // the simplest honest point; a cached/offline-grace Pro license is already
  // resolved here, and a not-yet-validated one honestly reports 'no'.
  useEffect(() => {
    const search = typeof window === 'undefined' ? '' : window.location.search;
    const origin = typeof window === 'undefined' ? '' : window.location.origin;
    const referrer = typeof document === 'undefined' ? '' : document.referrer;
    track('app_open', {
      mode: route === 'edit' ? 'studio' : 'convert',
      pro: isPro ? 'yes' : 'no',
      ref: refPathFrom(referrer, origin),
      to: parseConvertParams(search).to,
    });
    // Fire exactly once on mount; deliberately no deps so a later isPro change
    // (remote validation) does not emit a second app_open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tab selection from the rail (click or keyboard). A user-initiated mode
  // switch only — cue-jumps use setRoute directly and are not mode switches.
  const selectRoute = useCallback(
    (next: Route) => {
      if (next !== route) track('mode_switch', { to: next === 'edit' ? 'studio' : 'convert' });
      setRoute(next);
    },
    [route],
  );

  const goToCue = useCallback((index: number) => {
    setRoute('edit');
    setPendingCueJump(index);
  }, []);
  const clearCueJump = useCallback(() => setPendingCueJump(null), []);
  const nav = useMemo<NavValue>(
    () => ({ goToCue, pendingCueJump, clearCueJump }),
    [goToCue, pendingCueJump, clearCueJump],
  );

  // Reveal a cue when the QC cursor moves by intent (step/select/fix). navTarget
  // carries a seq that changes per move, so this fires once per move and never
  // on a background re-evaluation (which leaves seq untouched). A document-level
  // issue has cueIndex null — move the cursor but don't jump.
  useEffect(() => {
    if (navTarget.seq === 0) return;
    if (navTarget.cueIndex != null && navTarget.cueIndex >= 0) goToCue(navTarget.cueIndex);
  }, [navTarget, goToCue]);

  // Keyboard issue-stepping, reachable from any mode (the Inspector is global
  // chrome). Alt+Arrow / Alt+Enter, never hijacked while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || inTextField(e.target)) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        stepNext();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        stepPrev();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        // Alt+Shift+Enter accepts the current issue as intentional and advances;
        // plain Alt+Enter fixes and advances (cue-forge-n3e.10).
        if (e.shiftKey) acceptAndAdvance();
        else fixAndAdvance();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stepNext, stepPrev, fixAndAdvance, acceptAndAdvance]);

  return (
    <NavContext.Provider value={nav}>
      <div
        className={`cf-shell${showInspector ? '' : ' cf-shell--no-inspector'}${
          showInspector && inspectorCollapsed ? ' cf-shell--inspector-collapsed' : ''
        }`}
        style={{ '--cf-inspector-w': `${inspectorWidth}px` } as React.CSSProperties}
      >
        {/* First tab stop (cue-forge-drt.19): the rail + license form put ~4 stops
            before <main> for free users; a standard visually-hidden-until-focused
            skip link jumps straight to the work area. Explicit focus() because the
            shell has no router/hash navigation. */}
        <a
          className="cf-skip-link"
          href="#cf-main"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById('cf-main')?.focus();
          }}
        >
          Skip to main content
        </a>
        <Rail
          brand={BRAND.name}
          routes={ROUTES}
          labels={ROUTE_LABELS}
          active={route}
          onSelect={selectRoute}
          footer={
            <>
              <ThemeToggle />
              <LicensePanel />
              <AnalyticsToggle />
            </>
          }
        />
        {/* The tabpanels live inside RouteView — both modes stay mounted and the
            inactive one is `hidden`, so mode-local state survives Studio<->Convert
            hops (cue-forge-drt.14). */}
        <main className="cf-work" id="cf-main" tabIndex={-1}>
          <RestoreBanner />
          {/* Always-mounted polite live region for the window-drop multi-file
              notice (w28.14) — it must exist before the message is set. */}
          <div className="cf-shell__drop-notice" role="status" aria-live="polite">
            {dropNotice}
          </div>
          <WindowDropDelegateContext.Provider value={dropDelegate}>
            {/* Studio's EmptyState dropzone routes its drops through the same
                intake so a multi-file drop there matches the window net (same
                first-file load, same ignored-files notice above). */}
            <StudioFileDropContext.Provider value={onDroppedFiles}>
              <RouteView route={route} />
            </StudioFileDropContext.Provider>
          </WindowDropDelegateContext.Provider>
        </main>
        {showInspector && (
          <>
            <button
              type="button"
              className="cf-shell__issues-toggle"
              aria-expanded={inspectorOpen}
              // Only reference the panel while it is actually mounted (cue-forge-zxw.9,
              // mirroring the Rail's aria-controls guard).
              aria-controls={!inspectorCollapsed ? 'cf-inspector' : undefined}
              // The narrow drawer owns Inspector visibility, so opening it must also
              // clear a desktop collapse or the panel would stay unmounted and
              // unreachable below 960px (cue-forge-zxw.9).
              onClick={() => {
                setInspectorCollapsed(false);
                setInspectorOpen((open) => !open);
              }}
            >
              Issues{hasRun && issues.length > 0 ? ` (${issues.length})` : ''}
            </button>
            {inspectorCollapsed ? (
              // Desktop reveal tab (narrow viewports use the drawer toggle above).
              // No aria-controls: the panel is unmounted while collapsed, so a
              // reference would dangle (cue-forge-zxw.9).
              <button
                ref={revealRef}
                type="button"
                className="cf-shell__inspector-reveal"
                aria-label="Show issues panel"
                onClick={() => {
                  pendingInspectorFocus.current = 'inspector';
                  setInspectorCollapsed(false);
                }}
              >
                Issues{hasRun && issues.length > 0 ? ` (${issues.length})` : ''}
              </button>
            ) : (
              <>
                <div
                  className="cf-shell__resizer"
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize issues panel"
                  aria-valuemin={INSPECTOR_MIN_W}
                  aria-valuemax={INSPECTOR_MAX_W}
                  aria-valuenow={inspectorWidth}
                  tabIndex={0}
                  onPointerDown={onResizePointerDown}
                  onKeyDown={onResizeKeyDown}
                />
                <Inspector
                  id="cf-inspector"
                  className={inspectorOpen ? 'cf-inspector--open' : undefined}
                  onCollapse={() => {
                    pendingInspectorFocus.current = 'reveal';
                    setInspectorCollapsed(true);
                  }}
                />
              </>
            )}
          </>
        )}
        <ShortcutsHelp />
        <UpdateBanner apply={applyUpdate} />
      </div>
    </NavContext.Provider>
  );
}
